import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Row,
  Container,
  InputGroup,
  InputGroupText,
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import PaymentReportService from "../../../services/OrderingServices/PaymentReportService";
import toast from "react-hot-toast";
import CountUp from "react-countup";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import { Link } from "react-router-dom";
import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";
import CreditLogService from "../../../services/OrderingServices/CreditLogService";
import { Triangle } from "react-loader-spinner";

const PaymentDayBook = () => {
  const [loading, setLoading] = useState(false);
  const [paymentTypeList, setPaymentTypeList] = useState([]);
  const [expenseTypeList, setExpenseTypeList] = useState([]);
  const [summary, setSummary] = useState({
    totalEarnings: 0,
    totalExpenses: 0,
  });

  const today = new Date();
  const todayStr = today.toISOString().split("T")[0];

  const [filters, setFilters] = useState({
    startDate: todayStr,
    endDate: todayStr,
  });

  const [accordionOpen, setAccordionOpen] = useState("1");
  const [creditSummary, setCreditSummary] = useState({});

  const toggleAccordion = (id) => {
    setAccordionOpen(accordionOpen === id ? "" : id);
  };

  const fetchPaymentReport = async (start, end) => {
    if (!start || !end) return;

    setLoading(true);
    try {
      const response = await PaymentReportService.daybookList(start, end);

      setSummary({
        totalEarnings: response.totalEarnings || 0,
        totalExpenses: response.totalExpenses || 0,
      });
      setPaymentTypeList(response.paymentTypeEarnings || []);
      setExpenseTypeList(response.expenseTypes || []);
    } catch (error) {
      console.error("Error fetching day book:", error);
      toast.error("Failed to fetch day book report");
      setPaymentTypeList([]);
      setExpenseTypeList([]);
      setSummary({ totalEarnings: 0, totalExpenses: 0 });
    } finally {
      setLoading(false);
    }
  };
  const loadCreditSummary = async () => {
    const response = await CreditLogService.getSummary("all");
    const map = {};

    response.forEach((item) => {
      map[item.customerName] = item.customerId;
    });

    setCreditSummary(map);
  };
  useEffect(() => {
    loadCreditSummary();
  }, []);

  useEffect(() => {
    setFilters({ startDate: todayStr, endDate: todayStr });
    fetchPaymentReport(todayStr, todayStr);
  }, []);

  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleDateRangeChange = (selectedDates) => {
    if (selectedDates.length === 2) {
      const [start, end] = selectedDates;
      const startStr = formatLocalDate(start);
      const endStr = formatLocalDate(end);

      setFilters({
        startDate: startStr,
        endDate: endStr,
      });

      fetchPaymentReport(startStr, endStr);
    }
  };

  const earningsColumns = [
    { name: "S.N", selector: (_, i) => i + 1, width: "60px" },

    {
      name: "Payment Type",
      selector: (row) => row.paymentTypeName,
      cell: (row) => {
        // CASE 1: Not credit customer
        if (row.paymentTypeId !== 0) {
          return (
            <Link
              to="/payment/statement"
              state={{ paymentTypeId: row.paymentTypeId }}
            >
              {row.paymentTypeName}
            </Link>
          );
        }

        // CASE 2: Credit customer (paymentTypeId = 0)
        const customerId = creditSummary[row.paymentTypeName];

        return customerId ? (
          <Link to={`/credit-logs/customer/${customerId}`}>
            {row.paymentTypeName}
          </Link>
        ) : (
          row.paymentTypeName
        );
      },
    },

    {
      name: "Amount (Rs.)",
      selector: (row) => row.amount,
      cell: (row) => <NumberWithCommas number={row.amount} />,
      right: true,
      width: "150px",
    },
  ];

  const expenseColumns = [
    { name: "S.N", selector: (_, i) => i + 1, width: "60px" },
    {
      name: "Supplier / Expense Type",
      selector: (row) => row.supplierName || "N/A",
    },
    {
      name: "Amount (Rs.)",
      selector: (row) => row.amount,
      cell: (row) => <NumberWithCommas number={row.amount} />,
      right: true,
      width: "150px",
    },
  ];

  const SummaryCard = ({ title, value, color, icon }) => (
    <Card className="shadow-sm  border-0 mb-3">
      <CardBody className="d-flex align-items-center justify-content-between">
        <div className="w-100">
          <h6 className="fw-semibold text-uppercase" style={{ color }}>
            {title}
          </h6>
          <div className="d-flex justify-content-between w-100">
            <h4 className="fs-2 fw-bold" style={{ color }}>
              <CountUp start={0} end={value} duration={0} separator="," />
            </h4>
            <div
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{
                backgroundColor: color + "20",
                color,
                width: "36px",
                height: "36px",
              }}
            >
              <i className={`${icon} fs-5`} />
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb title="Day Book" pageTitle="Payment Records" />
        <Row className="mt-2">
          <Col lg={6} md={6} sm={12}>
            <SummaryCard
              title="Total Earnings"
              value={summary.totalEarnings}
              color="#28a745"
              icon="bx bx-receipt"
            />
          </Col>
          <Col lg={6} md={6} sm={12}>
            <SummaryCard
              title="Total Expenses"
              value={summary.totalExpenses}
              color="#cf3939ff"
              icon="bx bx-dollar-circle"
            />
          </Col>
        </Row>

        <div
          className="p-3 p-lg-4 mt-3"
          style={{
            background: "#ffffff",
            borderRadius: "0.5rem",
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            border: "1px solid #eee",
          }}
        >
          <div className="mb-3 d-flex justify-content-between align-items-center">
            <h5 className="mb-1 fw-bold text-dark">Payment Day Book</h5>
            <div className="d-flex align-items-center gap-3">
              <Col md={12}>
                <InputGroup style={{ width: "280px" }}>
                  <Flatpickr
                    className="form-control form-control-sm"
                    placeholder="Select Date or Range"
                    value={[filters.startDate, filters.endDate]}
                    onChange={handleDateRangeChange}
                    options={{
                      mode: "range",
                      dateFormat: "Y-m-d",
                      maxDate: "today",
                      defaultDate: [todayStr, todayStr],
                      allowInput: false,
                    }}
                  />
                  <InputGroupText style={{ cursor: "pointer" }}>
                    <i className="bx bx-calendar"></i>
                  </InputGroupText>
                </InputGroup>
              </Col>
            </div>
          </div>

          <Row>
            {/* Earnings Table */}
            <Col lg={6} md={12} sm={12} className="mb-lg-0 mb-4">
              <div className="mb-3">
                <h5
                  className="mb-0"
                  style={{
                    fontWeight: "600",
                    color: "#1a1a1a",
                    fontSize: "16px",
                  }}
                >
                  <i
                    className="bx bx-plus-circle me-2"
                    style={{ color: "#28a745" }}
                  ></i>
                  Earnings
                </h5>
              </div>
              <div
                style={{
                  border: "1px solid #e9ecef",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <CustomDataTable
                  columns={earningsColumns}
                  data={paymentTypeList}
                  noHeader
                  pagination={paymentTypeList.length > 0}
                  paginationPerPage={5}
                  paginationRowsPerPageOptions={[5, 10, 15, 20]}
                  striped
                  highlightOnHover
                  responsive
                  persistTableHead
                  customStyles={{
                    headRow: {
                      style: {
                        backgroundColor: "#f8f9fa",
                        fontWeight: "600",
                        fontSize: "13px",
                        color: "#495057",
                        borderBottom: "2px solid #dee2e6",
                      },
                    },
                    rows: {
                      style: {
                        fontSize: "14px",
                        minHeight: "52px",
                      },
                    },
                    pagination: {
                      style: {
                        borderTop: "1px solid #dee2e6",
                        minHeight: "56px",
                        fontSize: "13px",
                      },
                    },
                  }}
                  paginationComponentOptions={{
                    rowsPerPageText: "Rows:",
                    rangeSeparatorText: "of",
                  }}
                  noDataComponent={
                    <div className="text-center py-5">
                      <i
                        className="ri-inbox-line"
                        style={{ fontSize: "48px", color: "#dee2e6" }}
                      />
                      <p className="text-muted mt-3 mb-0">
                        No earnings data available
                      </p>
                    </div>
                  }
                  progressComponent={
                    <div className="my-3">
                      <Triangle
                        visible={true}
                        height="80"
                        width="80"
                        color="#5B71B9"
                        ariaLabel="triangle-loading"
                      />
                      <h5 className="mt-1">Loading...</h5>
                    </div>
                  }
                />
              </div>
            </Col>

            {/* Expenses Table */}
            <Col lg={6} md={12} sm={12}>
              <div className="mb-3">
                <h5
                  className="mb-0"
                  style={{
                    fontWeight: "600",
                    color: "#1a1a1a",
                    fontSize: "16px",
                  }}
                >
                  <i
                    className="bx bx-minus-circle me-2"
                    style={{ color: "#dc3545" }}
                  ></i>
                  Expenses
                </h5>
              </div>
              <div
                style={{
                  border: "1px solid #e9ecef",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <CustomDataTable
                  columns={expenseColumns}
                  data={expenseTypeList}
                  noHeader
                  pagination={expenseTypeList.length > 0}
                  paginationPerPage={5}
                  paginationRowsPerPageOptions={[5, 10, 15, 20]}
                  striped
                  highlightOnHover
                  responsive
                  persistTableHead
                  customStyles={{
                    headRow: {
                      style: {
                        backgroundColor: "#f8f9fa",
                        fontWeight: "600",
                        fontSize: "13px",
                        color: "#495057",
                        borderBottom: "2px solid #dee2e6",
                      },
                    },
                    rows: {
                      style: {
                        fontSize: "14px",
                        minHeight: "52px",
                      },
                    },
                    pagination: {
                      style: {
                        borderTop: "1px solid #dee2e6",
                        minHeight: "56px",
                        fontSize: "13px",
                      },
                    },
                  }}
                  paginationComponentOptions={{
                    rowsPerPageText: "Rows:",
                    rangeSeparatorText: "of",
                  }}
                  noDataComponent={
                    <div className="text-center py-5">
                      <i
                        className="ri-inbox-line"
                        style={{ fontSize: "48px", color: "#dee2e6" }}
                      />
                      <p className="text-muted mt-3 mb-0">
                        No expenses data available
                      </p>
                    </div>
                  }
                  progressComponent={
                    <div className="my-3">
                      <Triangle
                        visible={true}
                        height="80"
                        width="80"
                        color="#5B71B9"
                        ariaLabel="triangle-loading"
                      />
                      <h5 className="mt-1">Loading...</h5>
                    </div>
                  }
                />
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </Container>
  );
};

export default PaymentDayBook;
